import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  DollarSign,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Phone,
  Plus,
  Search,
  User,
  Users
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEmployees } from '../context/EmployeeContext';

const ContactDashboard: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Overview');
  const { employees, getEmployeeById, getManagerName } = useEmployees();
  const [selectedFilter] = useState('All users');
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    startDate: '',
    startTime: '',
    endTime: '',
    attendees: [] as string[],
    description: '',
    hostType: 'User'
  });
  const [newAttendeeEmail, setNewAttendeeEmail] = useState('');

  // Function to handle meeting booking and JSON download
  const handleMeetingBooking = () => {
    // Get the current contact information
    const currentContact = getEmployeeById(contactId || '');
    
    if (!currentContact) {
      alert('Contact not found');
      return;
    }

    // Get stored lead data from localStorage (if any)
    const storedLeadData = localStorage.getItem('pendingLeadData');
    let leadData = null;
    
    if (storedLeadData) {
      try {
        leadData = JSON.parse(storedLeadData);
      } catch (error) {
        console.error('Error parsing stored lead data:', error);
      }
    }

    // Create meeting data (without attendeeDescription)
    const meetingData = {
      contactName: currentContact.name,
      title: currentContact.jobTitle,
      startDate: meetingForm.startDate,
      startTime: meetingForm.startTime,
      endTime: meetingForm.endTime,
      attendees: meetingForm.attendees
    };

    // Combine lead data and meeting data
    const combinedData = {
      ...(leadData || {}), // Include lead data if available
      meeting: meetingData // Add meeting data under 'meeting' key
    };

    // Download the combined JSON file
    const blob = new Blob([JSON.stringify(combinedData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lead-meeting-combined.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Clear stored lead data after successful download
    localStorage.removeItem('pendingLeadData');

    // Close modal and reset form
    setShowMeetingModal(false);
    setMeetingForm({
      title: '',
      startDate: '',
      startTime: '',
      endTime: '',
      attendees: [],
      description: '',
      hostType: 'User'
    });
    setNewAttendeeEmail('');

    // Show success message
    // alert(`Meeting booked successfully for ${currentContact.name}! Combined lead and meeting data downloaded.`);
  };



  const employee = getEmployeeById(contactId || '') || employees[0];
  const tabs = ['Overview', 'Activities', 'Emails', 'Calls', 'Tasks', 'Meetings'];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <DollarSign className="h-8 w-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Revenue Generated</p>
                    <p className="text-2xl font-bold text-gray-900">$245K</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Team Size</p>
                    <p className="text-2xl font-bold text-gray-900">8</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Meetings This Week</p>
                    <p className="text-2xl font-bold text-gray-900">12</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Department</span>
                  <span className="text-gray-900">{employee.department}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Manager</span>
                  <span className="text-gray-900">{getManagerName(employee.managerId)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Hire Date</span>
                  <span className="text-gray-900">{employee.hireDate}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'Activities':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent Activities</h3>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search activities..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <span>{selectedFilter}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Team Meeting</p>
                      <p className="text-sm text-gray-600 mt-1">{employee.name} attended weekly sales team meeting</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">2 hours ago</span>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <Calendar className="h-4 w-4 text-green-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Performance Review</p>
                      <p className="text-sm text-gray-600 mt-1">Quarterly performance review completed with {getManagerName(employee.managerId)}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">1 week ago</span>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <User className="h-4 w-4 text-gray-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{employee.name} joined the team</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">{employee.hireDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'Emails':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Email Communications</h3>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-sm font-medium">{employee.initials}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Weekly Team Update</p>
                      <p className="text-xs text-gray-500">To: team@theta.com</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">2 days ago</span>
                </div>
                <p className="text-sm text-gray-600">Sent weekly progress update to the team regarding Q4 targets and upcoming client meetings...</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-sm font-medium">CL</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Client Follow-up</p>
                      <p className="text-xs text-gray-500">From: client@innovatech.com</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">1 week ago</span>
                </div>
                <p className="text-sm text-gray-600">Thank you for the proposal. We're reviewing internally and will get back to you by Friday...</p>
              </div>
            </div>
          </div>
        );

      case 'Calls':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Call History</h3>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Phone className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Client Discovery Call</p>
                      <p className="text-xs text-gray-500">Duration: 45 minutes</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">Yesterday</span>
                </div>
                <p className="text-sm text-gray-600">Discussed client requirements and presented initial solution overview. Next steps: Send proposal by EOW.</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Phone className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Team Standup</p>
                      <p className="text-xs text-gray-500">Duration: 30 minutes</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">3 days ago</span>
                </div>
                <p className="text-sm text-gray-600">Weekly team standup to discuss progress on active deals and upcoming priorities.</p>
              </div>
            </div>
          </div>
        );

      case 'Tasks':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Tasks & Follow-ups</h3>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <input type="checkbox" className="rounded border-gray-300" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Follow up with Innovatech Solutions</p>
                      <p className="text-xs text-gray-500">Due: Tomorrow</p>
                    </div>
                  </div>
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">High Priority</span>
                </div>
                <p className="text-sm text-gray-600">Send revised proposal based on feedback from discovery call.</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <input type="checkbox" className="rounded border-gray-300" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Prepare Q4 performance review</p>
                      <p className="text-xs text-gray-500">Due: Next week</p>
                    </div>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Medium Priority</span>
                </div>
                <p className="text-sm text-gray-600">Compile performance metrics and achievements for quarterly review.</p>
              </div>
            </div>
          </div>
        );

      case 'Meetings':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Upcoming Meetings</h3>
              <div className="flex items-center space-x-3">
                <button className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                  Log Meeting
                </button>
                <button 
                  onClick={() => setShowMeetingModal(true)}
                  className="px-4 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Create Meeting
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Client Presentation</p>
                      <p className="text-xs text-gray-500">Tomorrow at 2:00 PM</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Confirmed</span>
                </div>
                <p className="text-sm text-gray-600">Present final proposal to Innovatech Solutions leadership team.</p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Weekly Team Sync</p>
                      <p className="text-xs text-gray-500">Friday at 10:00 AM</p>
                    </div>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Recurring</span>
                </div>
                <p className="text-sm text-gray-600">Weekly team standup to discuss progress and upcoming priorities.</p>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Content not found</div>;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Contact Details */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <button
              onClick={() => navigate('/contacts')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to contacts
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-lg font-medium">{employee.initials}</span>
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-semibold text-gray-900">{employee.name}</h1>
                <p className="text-gray-600">{employee.jobTitle}</p>
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Contact Info */}
          <div className="p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-900">{employee.email}</span>
            </div>
            <div className="flex items-center space-x-3">
              <User className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-900">{employee.department}</span>
            </div>
          </div>
        </div>

        {/* Center Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Tab Navigation */}
          <div className="bg-white border-b border-gray-200 px-6">
            <div className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 text-sm font-medium border-b-2 ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {renderTabContent()}
          </div>
        </div>

        {/* Right Sidebar - Related Records */}
        <div className="w-80 bg-white border-l border-gray-200 p-6">
          <div className="space-y-6">
            {/* Active Deals */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Active Deals (3)</h3>
                <button className="text-blue-600 hover:text-blue-700">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Track deals currently being managed by this employee.
              </p>
            </div>

            {/* Performance Metrics */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Performance</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Deals Closed (Q4)</span>
                  <span className="text-gray-900">12</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Revenue Generated</span>
                  <span className="text-gray-900">$245K</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Quota Attainment</span>
                  <span className="text-green-600">108%</span>
                </div>
              </div>
            </div>

            {/* Team */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Direct Reports (2)</h3>
              </div>
              <p className="text-sm text-gray-500">
                Team members reporting to this employee.
              </p>
            </div>

            {/* Tasks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Open Tasks (5)</h3>
                <button className="text-blue-600 hover:text-blue-700">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Track tasks and follow-ups assigned to this employee.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Meeting Creation Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Schedule</h2>
              <button 
                onClick={() => setShowMeetingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Plus className="h-6 w-6 transform rotate-45" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex">
              {/* Left Panel - Form */}
              <div className="w-1/2 p-6 space-y-6">
                {/* Host Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Host</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="hostType" 
                        value="User"
                        checked={meetingForm.hostType === 'User'}
                        onChange={(e) => setMeetingForm({...meetingForm, hostType: e.target.value})}
                        className="mr-2"
                      />
                      <span className="text-sm">User</span>
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio" 
                        name="hostType" 
                        value="Meeting rotation"
                        checked={meetingForm.hostType === 'Meeting rotation'}
                        onChange={(e) => setMeetingForm({...meetingForm, hostType: e.target.value})}
                        className="mr-2"
                      />
                      <span className="text-sm">Meeting rotation</span>
                      <span className="ml-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">NEW</span>
                    </label>
                  </div>
                </div>
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={meetingForm.title}
                    onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Meeting title"
                  />
                </div>
                
                {/* Date and Time */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start date</label>
                    <input
                      type="date"
                      value={meetingForm.startDate}
                      onChange={(e) => setMeetingForm({...meetingForm, startDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start time</label>
                    <input
                      type="time"
                      value={meetingForm.startTime}
                      onChange={(e) => setMeetingForm({...meetingForm, startTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End time</label>
                    <input
                      type="time"
                      value={meetingForm.endTime}
                      onChange={(e) => setMeetingForm({...meetingForm, endTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
                
                {/* Attendees */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attendees</label>
                  <div className="space-y-2">
                    {/* Display added attendees */}
                    {meetingForm.attendees.map((email, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                        <span className="text-sm text-gray-700">{email}</span>
                        <button
                          onClick={() => {
                            const newAttendees = meetingForm.attendees.filter((_, i) => i !== index);
                            setMeetingForm({...meetingForm, attendees: newAttendees});
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Plus className="h-4 w-4 transform rotate-45" />
                        </button>
                      </div>
                    ))}
                    
                    {/* Add new attendee */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={newAttendeeEmail}
                        onChange={(e) => setNewAttendeeEmail(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Add email"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newAttendeeEmail.trim()) {
                            setMeetingForm({
                              ...meetingForm, 
                              attendees: [...meetingForm.attendees, newAttendeeEmail.trim()]
                            });
                            setNewAttendeeEmail('');
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          if (newAttendeeEmail.trim()) {
                            setMeetingForm({
                              ...meetingForm, 
                              attendees: [...meetingForm.attendees, newAttendeeEmail.trim()]
                            });
                            setNewAttendeeEmail('');
                          }
                        }}
                        className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Scheduled reminder emails */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Scheduled reminder emails</label>
                    <Plus className="h-4 w-4 text-blue-600 cursor-pointer" />
                  </div>
                  <p className="text-sm text-blue-600 cursor-pointer">+ Add reminder</p>
                </div>
                
                {/* Attendee description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attendee description</label>
                  <textarea
                    value={meetingForm.description}
                    onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows={3}
                    placeholder="Send a description to your attendees..."
                  />
                  {/* Rich text formatting toolbar */}
                  <div className="flex items-center space-x-2 mt-2 text-gray-500">
                    <button className="p-1 hover:bg-gray-100 rounded">B</button>
                    <button className="p-1 hover:bg-gray-100 rounded italic">I</button>
                    <button className="p-1 hover:bg-gray-100 rounded underline">U</button>
                    <button className="p-1 hover:bg-gray-100 rounded">≡</button>
                    <button className="p-1 hover:bg-gray-100 rounded">🔗</button>
                    <span className="text-xs">More</span>
                    <ChevronDown className="h-3 w-3" />
                  </div>
                </div>
                
                {/* Associated records */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Associated with 1 record</label>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              {/* Right Panel - Calendar */}
              <div className="w-1/2 p-6 bg-gray-50 border-l border-gray-200">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Jul 7 - Jul 11, 2025</h3>
                    <div className="flex items-center space-x-2">
                      <button className="p-1 hover:bg-gray-200 rounded">
                        <ChevronDown className="h-4 w-4 transform rotate-90" />
                      </button>
                      <button className="p-1 hover:bg-gray-200 rounded">
                        <ChevronDown className="h-4 w-4 transform -rotate-90" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <input type="checkbox" className="rounded" />
                    <label className="text-sm text-gray-600">Hide weekends</label>
                    <span className="text-xs text-blue-600 ml-auto">UTC -08:00 Pacific Time</span>
                    <ChevronDown className="h-3 w-3 text-gray-400" />
                  </div>
                </div>
                
                {/* Calendar Grid */}
                <div className="bg-white rounded border">
                  {/* Calendar Header */}
                  <div className="flex border-b">
                    <div className="w-20 p-2 text-xs font-medium text-gray-600 border-r"></div>
                    {['MON 7', 'TUE 8', 'WED 9', 'THU 10', 'FRI 11'].map((day) => (
                      <div key={day} className="flex-1 p-2 text-xs font-medium text-gray-600 text-center border-r last:border-r-0">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Time Slots */}
                  <div className="max-h-64 overflow-y-auto">
                    {['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map((time) => (
                      <div key={time} className="flex border-b last:border-b-0">
                        <div className="w-20 p-2 text-xs text-gray-500 border-r flex items-center">{time}</div>
                        {[0, 1, 2, 3, 4].map((col) => (
                          <div 
                            key={col} 
                            className="flex-1 h-10 border-r last:border-r-0 hover:bg-blue-50 cursor-pointer relative flex items-center justify-center"
                            onClick={() => {
                              // Handle time slot selection
                              const timeValue = time.replace(' AM', '').replace(' PM', '');
                              const hour = time.includes('PM') && !time.includes('12:') 
                                ? (parseInt(timeValue.split(':')[0]) + 12).toString().padStart(2, '0')
                                : timeValue.split(':')[0].padStart(2, '0');
                              const minute = timeValue.split(':')[1] || '00';
                              setMeetingForm({...meetingForm, startTime: `${hour}:${minute}`});
                            }}
                          >
                            {/* Show existing meetings or busy times */}
                            {col === 1 && time === '11:00 AM' && (
                              <div className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded">
                                Busy
                              </div>
                            )}
                            {col === 3 && time === '2:00 PM' && (
                              <div className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                                Meeting
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button 
                onClick={() => setShowMeetingModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleMeetingBooking}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700"
              >
                Book Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDashboard;
